// One viewer's archived games, most recent first, each row opening to show the full seating.

import { ChevronDown, ScrollText, Skull } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";
import { playerWon, seatOf, storytold, type HistoryGame, type HistoryPlayer } from "@cc/shared";
import { characterIcon, formatDate } from "../../lib/history.js";
import { cn, teamColor, teamLabel } from "../../lib/ui.js";
import { Dialog, DialogContent, DialogTrigger, Tooltip } from "../ui/primitives.js";

type Props = {
  games: HistoryGame[];
  viewerId: string;
  emptyText: string;
  /** Scrolls inside its own box past this height; omit to let the page scroll instead. */
  maxHeightClass?: string;
};

const th = "sticky top-0 bg-ink-soft px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-parchment-dim";
const td = "px-2 py-1.5 align-middle";

function RoleCell({ player }: { player: HistoryPlayer | null }) {
  if (!player) return <span className="text-parchment-dim">—</span>;
  if (!player.characterId) return <span className="text-parchment-dim">No role</span>;
  const icon = characterIcon(player.characterId);
  return (
    <span className="flex items-center gap-1.5">
      {icon ? (
        <img src={icon} alt="" className="size-7 shrink-0 object-contain" />
      ) : (
        <span className="inline-block size-7 shrink-0 rounded-full border border-ink-line" />
      )}
      <span className="truncate">{player.characterName ?? player.characterId}</span>
    </span>
  );
}

function TeamDot({ team }: { team: HistoryPlayer["team"] }) {
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ background: teamColor(team) }} />
      <span className="text-parchment-dim">{teamLabel(team)}</span>
    </span>
  );
}

function Badge({ tone, children }: { tone: "win" | "loss" | "neutral" | "good" | "evil"; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-block rounded px-1.5 py-0.5 text-[11px] font-medium",
        tone === "win" && "bg-win/20 text-parchment",
        tone === "loss" && "bg-evil/20 text-parchment",
        tone === "good" && "bg-good/20 text-parchment",
        tone === "evil" && "bg-evil/20 text-parchment",
        tone === "neutral" && "bg-ink-line/60 text-parchment-dim",
      )}
    >
      {children}
    </span>
  );
}

function Winner({ team }: { team: HistoryGame["winningTeam"] }) {
  if (team === "good") return <Badge tone="good">Good</Badge>;
  if (team === "evil") return <Badge tone="evil">Evil</Badge>;
  return <Badge tone="neutral">Unrecorded</Badge>;
}

function Result({ game, viewerId }: { game: HistoryGame; viewerId: string }) {
  const me = seatOf(game, viewerId);
  if (!me) return storytold(game, viewerId) ? <Badge tone="neutral">Storyteller</Badge> : null;
  const won = playerWon(me, game);
  if (won === true) return <Badge tone="win">Won</Badge>;
  if (won === false) return <Badge tone="loss">Lost</Badge>;
  return (
    <Tooltip content={me.alignment ? "No winner was recorded." : "No side was recorded for this seat."}>
      <span>
        <Badge tone="neutral">—</Badge>
      </span>
    </Tooltip>
  );
}

const SIDE_COLUMN: { key: "good" | "evil" | "other"; label: string; dotClass: string }[] = [
  { key: "good", label: "Good", dotClass: "bg-good" },
  { key: "evil", label: "Evil", dotClass: "bg-evil" },
  { key: "other", label: "Other", dotClass: "bg-ochre" },
];

function PlayerRow({ player, viewerId }: { player: HistoryPlayer; viewerId: string }) {
  const icon = characterIcon(player.characterId);
  const dead = !player.aliveAtEnd;
  return (
    <li
      className={cn(
        "flex items-center gap-2 rounded px-1.5 py-1",
        player.profileId === viewerId && "bg-brass/10",
      )}
    >
      <span className="w-4 shrink-0 text-right tabular-nums text-parchment-dim">{player.seatIndex + 1}</span>
      {icon ? (
        <img src={icon} alt="" className={cn("size-7 shrink-0 object-contain", dead && "grayscale")} />
      ) : (
        <span className="inline-block size-7 shrink-0 rounded-full border border-ink-line" />
      )}
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-parchment", dead && "text-parchment-dim line-through")}>
          {player.displayName}
          {player.isTraveler && <span className="text-parchment-dim"> · Traveler</span>}
        </span>
        <span className="block truncate text-[11px] text-parchment-dim">
          {player.characterName ?? (player.characterId ? player.characterId : "No role")}
        </span>
      </span>
      {dead && (
        <span className="flex shrink-0 items-center gap-1 rounded bg-evil/25 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-parchment">
          <Skull className="size-3" aria-hidden />
          Dead
        </span>
      )}
    </li>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[11px] uppercase tracking-wide text-parchment-dim">{label}</span>
      <span className="text-parchment">{children}</span>
    </div>
  );
}

function Expanded({ game, viewerId }: { game: HistoryGame; viewerId: string }) {
  const bySide = new Map<"good" | "evil" | "other", HistoryPlayer[]>();
  for (const player of game.players) {
    const side = player.alignment ?? "other";
    const list = bySide.get(side) ?? [];
    list.push(player);
    bySide.set(side, list);
  }

  return (
    <div className="space-y-4 border-t border-ink-line/60 bg-ink px-3 py-3 text-xs">
      <div className="grid gap-4 sm:grid-cols-3">
        {SIDE_COLUMN.map(({ key, label, dotClass }) => {
          const players = bySide.get(key) ?? [];
          if (players.length === 0) return null;
          return (
            <div key={key}>
              <h3 className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-parchment-dim">
                <span aria-hidden className={cn("inline-block size-2 rounded-full", dotClass)} />
                {label}
              </h3>
              <ul className="m-0 list-none space-y-0.5 p-0">
                {players.map((p) => (
                  <PlayerRow key={p.profileId + p.seatIndex} player={p} viewerId={viewerId} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 border-t border-ink-line/60 pt-3">
        <InfoRow label={game.storytellers.length === 1 ? "Storyteller" : "Storytellers"}>
          {game.storytellers.length > 0 ? game.storytellers.map((s) => s.displayName).join(", ") : "None recorded"}
        </InfoRow>
        <InfoRow label="Winner">
          <Winner team={game.winningTeam} />
        </InfoRow>
        <InfoRow label="Code">
          <span className="font-mono">{game.code}</span>
        </InfoRow>
        {game.storytellerNotes && (
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="ml-auto flex shrink-0 items-center gap-1 rounded border border-ink-line px-1.5 py-0.5 text-[11px] text-parchment-dim hover:border-brass/50 hover:text-parchment"
              >
                <ScrollText className="size-3" aria-hidden />
                Notes
              </button>
            </DialogTrigger>
            <DialogContent title="Storyteller notes">
              <p className="m-0 whitespace-pre-wrap text-sm text-parchment">{game.storytellerNotes}</p>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

export function GameTable({ games, viewerId, emptyText, maxHeightClass }: Props) {
  const [open, setOpen] = useState<string | null>(null);

  if (games.length === 0) {
    return <p className="py-8 text-center text-xs text-parchment-dim">{emptyText}</p>;
  }

  return (
    <div className={cn("cc-scroll overflow-auto", maxHeightClass)}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={th}>Date</th>
            <th className={th}>Script</th>
            <th className={th}>Role</th>
            <th className={th}>Team</th>
            <th className={th}>Result</th>
            <th className={th}>Survived</th>
            <th className={cn(th, "w-8")}>
              <span className="sr-only">Details</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {games.map((game) => {
            const expanded = open === game.id;
            const me = seatOf(game, viewerId);
            return (
              <Fragment key={game.id}>
                <tr
                  className={cn(
                    "cursor-pointer border-t border-ink-line/60 hover:bg-ink/60",
                    expanded && "bg-ink/60",
                  )}
                  onClick={() => setOpen(expanded ? null : game.id)}
                >
                  <td className={cn(td, "whitespace-nowrap text-parchment-dim")}>{formatDate(game.endedAt)}</td>
                  <td className={cn(td, "max-w-40 truncate text-parchment")}>{game.scriptName}</td>
                  <td className={cn(td, "max-w-44")}>
                    {me ? <RoleCell player={me} /> : <span className="text-parchment-dim">—</span>}
                  </td>
                  <td className={td}>{me ? <TeamDot team={me.team} /> : <span className="text-parchment-dim">—</span>}</td>
                  <td className={td}>
                    <Result game={game} viewerId={viewerId} />
                  </td>
                  <td className={cn(td, "text-parchment-dim")}>{me ? (me.aliveAtEnd ? "Yes" : "No") : "—"}</td>
                  <td className={cn(td, "text-right")}>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-label={expanded ? "Hide details" : "Show details"}
                      className="rounded p-0.5 text-parchment-dim hover:text-parchment focus-visible:outline-2 focus-visible:outline-brass"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(expanded ? null : game.id);
                      }}
                    >
                      <ChevronDown className={cn("size-4 transition-transform", expanded && "rotate-180")} />
                    </button>
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <Expanded game={game} viewerId={viewerId} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
